'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import Link from 'next/link';
import { reportClientError } from '@/lib/report-client-error';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render crashes inside Privy/wagmi mount (often extension
 * conflicts) so `/` paints a recovery card instead of a hard blank —
 * parity with `/debug` which intentionally skips this subtree.
 */
export class PrivyBootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      source: 'privy-boot-error-boundary',
      error,
      componentStack: info.componentStack ?? null,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="mx-auto flex max-w-lg flex-col gap-4 p-8 text-white"
          style={{ backgroundColor: '#07050a', minHeight: '100vh' }}
        >
          <h1 className="text-xl font-semibold">Wallet / login failed to load</h1>
          <p className="text-sm text-zinc-400">
            A browser wallet extension conflict or a corrupted auth session
            blocked the lobby from finishing startup. Copy diagnostics from{' '}
            <Link href="/debug" className="text-amber-400 underline">
              /debug
            </Link>{' '}
            for support, or try again without extensions enabled.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              href="/debug"
              className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-black"
            >
              Open diagnostics
            </Link>
            <button
              type="button"
              className="rounded-md border border-zinc-600 px-4 py-2 text-sm font-semibold"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
